
import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { CheckCircle, Loader2, AlertCircle } from 'lucide-react';
import { getCurrentSubscription } from '@/services/subscriptions';
import { useToast } from '@/hooks/use-toast';
import { supabase } from '@/integrations/supabase/client';

const SubscriptionSuccess = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { toast } = useToast();
  const [isProcessing, setIsProcessing] = useState(true);
  const [pollCount, setPollCount] = useState(0);
  const [status, setStatus] = useState<'processing' | 'verified' | 'timeout'>('processing');
  const [sessionReady, setSessionReady] = useState(false);
  
  useEffect(() => {
    let unsub: (() => void) | undefined;
    const init = async () => {
      const { data: { session } } = await supabase.auth.getSession();
      if (session) {
        setSessionReady(true);
        return;
      }
      const { data: listener } = supabase.auth.onAuthStateChange((_event, sess) => {
        if (sess) setSessionReady(true);
      });
      unsub = listener?.subscription?.unsubscribe;
    };
    init();
    return () => { if (unsub) unsub(); };
  }, []);
  
  useEffect(() => {
    if (!sessionReady) return;
    
    let pollInterval: ReturnType<typeof setInterval>;
    let attempts = 0;
    const maxAttempts = 30; // 60 seconds total (30 attempts × 2 seconds)
    
    const initializeAndPoll = async () => {
      try {
        // Get order ID from URL or subscription
        const orderIdFromUrl = searchParams.get('order_id');
        const sidFromUrl = searchParams.get('sid');
        const sub = await getCurrentSubscription();
        const fallbackOrderId = sub?.stripeSubscriptionId || null;
        const orderId = (orderIdFromUrl && orderIdFromUrl !== '{PUBLIC_ID}') ? orderIdFromUrl : fallbackOrderId;
        const subscriptionId = sidFromUrl || sub?.id || null;
        
        if (!orderId && !subscriptionId) {
          navigate('/subscription');
          return;
        }
        
        console.log('Initializing with order ID:', orderId);
        setStatus('processing');
        setIsProcessing(true);
        
        const checkStatus = async () => {
          try {
            // Try verification immediately and again on attempt 3 as a fallback
            if (attempts === 0 || attempts === 3) {
              console.log('Calling verify-revolut-order edge function...');
              try {
                await supabase.functions.invoke('verify-revolut-order', {
                  body: { order_id: orderId, subscription_id: subscriptionId }
                });
              } catch (verifyError) {
                console.error('Verify edge function error:', verifyError);
              }
            }
            
            // Check subscription status
            const fresh = await getCurrentSubscription();
            
            if (fresh?.status === 'active') {
              console.log('Subscription verified as active');
              setStatus('verified');
              setIsProcessing(false);
              if (pollInterval) clearInterval(pollInterval);
              
              toast({
                title: "Subscription Activated",
                description: "Your subscription is now active!",
              });
              
              setTimeout(() => {
                navigate('/app?payment_success=true');
              }, 2000);
            } else if (attempts >= maxAttempts - 1) {
              console.warn('Max attempts reached');
              setStatus('timeout');
              setIsProcessing(false);
              if (pollInterval) clearInterval(pollInterval);
            } else {
              attempts++;
              setPollCount(attempts);
            }
          } catch (error) {
            console.error('Error checking status:', error);
            attempts++;
            if (attempts >= maxAttempts) {
              setStatus('timeout');
              setIsProcessing(false);
              if (pollInterval) clearInterval(pollInterval);
            }
          }
        };
        
        // Check immediately
        await checkStatus();
        
        // Then poll every 2 seconds
        pollInterval = setInterval(checkStatus, 2000);
        
      } catch (error) {
        console.error('Initialization error:', error);
        navigate('/subscription');
      }
    };
    
    initializeAndPoll();
    
    return () => {
      if (pollInterval) clearInterval(pollInterval);
    };
  }, [searchParams, navigate, toast, sessionReady]);
  
  const handleManualRefresh = async () => {
    try {
      setIsProcessing(true);
      setPollCount(0);
      setStatus('processing');

      // Derive IDs again
      const orderIdFromUrl = searchParams.get('order_id');
      const sidFromUrl = searchParams.get('sid');
      const sub = await getCurrentSubscription();
      const fallbackOrderId = sub?.stripeSubscriptionId || null;
      const orderId = (orderIdFromUrl && orderIdFromUrl !== '{PUBLIC_ID}') ? orderIdFromUrl : fallbackOrderId;
      const subscriptionId = sidFromUrl || sub?.id || null;

      if (!orderId && !subscriptionId) {
        navigate('/subscription');
        return;
      }

      await supabase.functions.invoke('verify-revolut-order', {
        body: { order_id: orderId, subscription_id: subscriptionId }
      });

      const fresh = await getCurrentSubscription();
      if (fresh?.status === 'active') {
        setStatus('verified');
        setIsProcessing(false);
        toast({
          title: 'Subscription Activated',
          description: 'Your subscription is now active!',
        });
        setTimeout(() => {
          navigate('/app?payment_success=true');
        }, 1200);
      } else {
        setIsProcessing(false);
        toast({
          title: 'Still processing',
          description: 'Please try again in a few seconds.',
        });
      }
    } catch (err) {
      console.error('Manual verification error:', err);
      setIsProcessing(false);
    }
  };
  
  return (
    <div className="min-h-screen flex items-center justify-center bg-background p-4">
      <Card className="w-full max-w-md">
        <CardHeader className="text-center">
          {status === 'processing' ? (
            <div className="flex flex-col items-center">
              <Loader2 className="h-12 w-12 text-primary animate-spin mb-2" />
              <CardTitle className="text-xl">Processing your subscription...</CardTitle>
            </div>
          ) : status === 'verified' ? (
            <div className="flex flex-col items-center">
              <CheckCircle className="h-12 w-12 text-green-500 mb-2" />
              <CardTitle className="text-xl">Subscription Successful!</CardTitle>
            </div>
          ) : (
            <div className="flex flex-col items-center">
              <AlertCircle className="h-12 w-12 text-yellow-500 mb-2" />
              <CardTitle className="text-xl">Taking Longer Than Expected</CardTitle>
            </div>
          )}
          <CardDescription>
            {status === 'processing' 
              ? "We're confirming your subscription details. This may take a moment."
              : status === 'verified'
              ? "Thank you for your subscription. Your account has been updated."
              : "Your payment was received but we're still processing your subscription."}
          </CardDescription>
        </CardHeader>
        <CardContent className="text-center">
          {status === 'verified' && (
            <p className="text-muted-foreground">
              Your subscription is now active. You can now enjoy all the benefits of your plan.
            </p>
          )}
          {status === 'timeout' && (
            <p className="text-muted-foreground">
              Don't worry! Your payment was successful. Please check your subscription page or try refreshing.
            </p>
          )}
          {status === 'processing' && pollCount > 10 && (
            <p className="text-sm text-muted-foreground mt-4">
              Still processing... This is taking longer than usual.
            </p>
          )}
        </CardContent>
        <CardFooter className="flex justify-center gap-2">
          {status === 'timeout' && (
            <Button 
              onClick={handleManualRefresh}
              variant="outline"
            >
              Refresh Status
            </Button>
          )}
          {pollCount > 10 && status === 'processing' && (
            <Button 
              onClick={() => navigate('/app?payment_success=true')}
              variant="outline"
            >
              Continue Anyway
            </Button>
          )}
          <Button 
            onClick={() => navigate('/app?payment_success=true')} 
            disabled={status === 'processing'}
          >
            {status === 'processing' ? 'Please wait...' : 'Go to Home'}
          </Button>
        </CardFooter>
      </Card>
    </div>
  );
};

export default SubscriptionSuccess;
